import Vue from 'vue';
import Router, { Route } from 'vue-router';
import cloneDeep from 'clone-deep';

import Home from '@/views/Home.vue';
import Service from '@/views/Service.vue';
import ServiceBody from '@/views/ServiceBody.vue';
import FAQ from '@/views/FAQ.vue';
import Profile from '@/views/Profile.vue';
import AltLayout from '@/views/alternativeServices/AltLayout.vue';
import AltRefusal from '@/views/alternativeServices/AltRefusal/AltRefusal.vue';
import AltRegion from '@/views/alternativeServices/AltRegion.vue';

import AddDocumentsApplicant from '@/views/alternativeServices/AddDocuments/AddDocumentsApplicant.vue';
import AddDocumentsEdit from '@/views/alternativeServices/AddDocuments/AddDocumentsEdit.vue';
import AddDocumentsSign from '@/views/alternativeServices/AddDocuments/AddDocumentsSign.vue';

import ResendToFsspApplicant from '@/views/alternativeServices/ResendToFssp/ResendToFsspApplicant.vue';
import ResendToFsspSign from '@/views/alternativeServices/ResendToFssp/ResendToFsspSign.vue';

import AdditionalSigners from '@/views/alternativeServices/AdditionalSigners.vue';
import StatementDetail from '@/components/profile/statements/StatementDetail.vue';
import FormView from '@/components/elements/statement/FullStatementView/FormView.vue';

import StatementsJoint from '@/components/profile/statements/StatementsJoint.vue';
import OrganizationStatements from '@/components/profile/statements/OrganizationStatements.vue';
import PersonalDetails from '@/components/profile/personal-details/PersonalDetails.vue';
import MyStatements from '@/components/profile/statements/MyStatements.vue';
import Notifications from '@/components/profile/notifications/Notifications.vue';
import NotificationDetail from '@/components/profile/notifications/NotificationDetail.vue';
import DocumentsAndData from '@/components/profile/documents/DocumentsAndData.vue';
import NotificationsDocuments from '@/components/profile/notifications-documents/index.vue';
import NotificationsDocumentsDetail from '@/components/profile/notifications-documents/NotificationsDocumentsDetail.vue';
import { NotificationModule } from '@/store/modules/notifications';
import { NotificationDocModule } from '@/store/modules/notificationsDocuments';
import { AuthModule } from '@/store/modules/auth';
import { ErrorAlertModule } from '@/store/modules/errors';
import { OtherModule } from '@/store/modules/other';
import { StatementType } from '@/types/services';
import { mapStatementName } from '@/utils/services';
import { BreadCrumbsModule } from '@/store/modules/breadCrumbs';

Vue.use(Router);

export const RouteName = {
  HOME: 'home',
  SERVICE: 'service',
  SERVICE_ADDITIONAL: 'serviceAdditional',
  PROFILE: 'profile',
  [StatementType.NOTICE_RECOVERY]: StatementType.NOTICE_RECOVERY,
  [StatementType.RUZDI]: StatementType.RUZDI,
  [StatementType.SECURING_EVIDENCE]: StatementType.SECURING_EVIDENCE,
  [StatementType.ACCEPTANCE_DEPOSIT]: StatementType.ACCEPTANCE_DEPOSIT,
  [StatementType.TRANSFER_EL_DOCS]: StatementType.TRANSFER_EL_DOCS,
  [StatementType.CORRECT_TRANSLATION]: StatementType.CORRECT_TRANSLATION,
  [StatementType.ACCEPTANCE_ESCROW]: StatementType.ACCEPTANCE_ESCROW,
  [StatementType.STORAGE_ACCEPT]: StatementType.STORAGE_ACCEPT,
  [StatementType.STORAGE_ISSUE]: StatementType.STORAGE_ISSUE,
  [StatementType.DOCUMENT_CONVERSION]: StatementType.DOCUMENT_CONVERSION,
  [StatementType.ROD]: StatementType.ROD,
  STATEMENTS: 'Statements',
  STATEMENTS_JOINT: 'StatementsJoint',
  ORGANIZATION_STATEMENTS: 'OrganizationStatements',
  STATEMENT_DETAIL: 'StatementDetail',
  FORM_VIEW: 'FormView',
  NOTIFICATIONS: 'Notifications',
  NOTIFICATION_DETAIL: 'NotificationDetail',
  DOCUMENTS_AND_DATA: 'DocumentsAndData',
  FAQ: 'faq',
  NOTIFICATIONS_DOCUMENTS: 'NotificationsDocuments',
  NOTIFICATIONS_DOCUMENTS_DETAIL: 'NotificationsDocumentsDetail',
  ALTERNATIVE_SERVICE_REFUSAL: 'ALTERNATIVE_SERVICE_REFUSAL',
  ALTERNATIVE_SERVICE_DISTRICT: 'ALTERNATIVE_SERVICE_DISTRICT',
  ADD_DOCUMENTS_APPLICANT: 'ADD_DOCUMENTS_APPLICANT',
  ADD_DOCUMENTS_EDIT: 'ADD_DOCUMENTS_EDIT',
  ADD_DOCUMENTS_SIGN: 'ADD_DOCUMENTS_SIGN',
  RESEND_TO_FSSP_APPLICANT: 'RESEND_TO_FSSP_APPLICANT',
  RESEND_TO_FSSP_SIGN: 'RESEND_TO_FSSP_SIGN',
  ALTERNATIVE_SERVICE_EDIT: 'alternativeServiceEdit',
  ALTERNATIVE_SERVICE_RECEND: 'alternativeServiceRecend',
  ADDITIONAL_SIGNERS: 'ADDITIONAL_SIGNERS',
  PERSONAL_DETAILS: 'PersonalDetails',
  EXTRACTION_ROD: 'ExtractionRod',
  EGRN_CONFIRM: 'EGRN_CONFIRM',
  SURCHARGE: 'SURCHARGE',
};

export const RoutePath = {
  HOME: '/',
  STATEMENTS: '/profile/statements',
};

export const RouteText = {
  HOME: 'Главная',
  STATEMENTS: 'Мои заявления',
  STATEMENTS_JOINT: 'Совместные заявления',
  ORGANIZATION_STATEMENTS: 'Все заявления по организации',
  NOTIFICATIONS: 'Уведомления',
  NOTIFICATIONS_DOCUMENTS: 'Полученные документы',
  DOCUMENTS_AND_DATA: 'Профиль',
  STATEMENT_DETAIL: 'Детали заявления',
  ALTERNATIVE_SERVICE_REFUSAL: 'Отзыв заявления',
  ALTERNATIVE_SERVICE_DISTRICT: 'Смена нотариального округа',
  ADD_DOCUMENTS: 'Передача дополнительных документов',
  RESEND_TO_FSSP:
    'Заявление о повторной отправке Исполнительной надписи в Федеральную службу судебных приставов',
  ALTERNATIVE_SERVICE_RECEND: 'Повторная отправка Исполнительной надписи',
  ADDITIONAL_SIGNERS: 'Детали заявления',
  PERSONAL_DETAILS: 'Реквизиты',
};

export const Layout = {
  DEFAULT: 'Default',
  INNER: 'Inner',
};

const getServiceProps = (statementType: StatementType) => (route: Route) => ({
  statementId: route.params.statementId,
  statementType,
});
const getAlternativeServiceEditProps = (statementType: string) => (
  route: Route
) => ({
  originalStatementId: route.params.originalStatementId,
  statementType,
});

const serviceChildren = [
  {
    path: 'notice-recovery/:statementId?',
    name: RouteName[StatementType.NOTICE_RECOVERY],
    props: getServiceProps(StatementType.NOTICE_RECOVERY),
    meta: {
      layout: Layout.INNER,
      authorized: true,
      breadCrumbs: {
        back: true,
        path: [
          {
            text: RouteText.HOME,
            disabled: false,
            href: RoutePath.HOME,
          },
          {
            text: RouteText.STATEMENTS,
            disabled: false,
            href: RoutePath.STATEMENTS,
          },
          {
            text: mapStatementName[StatementType.NOTICE_RECOVERY],
            disabled: true,
          },
        ],
      },
    },
    component: ServiceBody,
  },
  {
    path: 'ruzdi/:statementId?',
    name: RouteName[StatementType.RUZDI],
    props: getServiceProps(StatementType.RUZDI),
    meta: {
      layout: Layout.INNER,
      authorized: true,
      breadCrumbs: {
        back: true,
        path: [
          {
            text: RouteText.HOME,
            disabled: false,
            href: RoutePath.HOME,
          },
          {
            text: RouteText.STATEMENTS,
            disabled: false,
            href: RoutePath.STATEMENTS,
          },
          {
            text: mapStatementName[StatementType.RUZDI],
            disabled: true,
          },
        ],
      },
    },
    component: ServiceBody,
  },
  {
    path: 'securing-evidence/:statementId?',
    name: RouteName[StatementType.SECURING_EVIDENCE],
    props: getServiceProps(StatementType.SECURING_EVIDENCE),
    meta: {
      layout: Layout.INNER,
      authorized: true,
      breadCrumbs: {
        back: true,
        path: [
          {
            text: RouteText.HOME,
            disabled: false,
            href: RoutePath.HOME,
          },
          {
            text: RouteText.STATEMENTS,
            disabled: false,
            href: RoutePath.STATEMENTS,
          },
          {
            text: mapStatementName[StatementType.SECURING_EVIDENCE],
            disabled: true,
          },
        ],
      },
    },
    component: ServiceBody,
  },
  {
    path: 'acceptance-deposit/:statementId?',
    name: RouteName[StatementType.ACCEPTANCE_DEPOSIT],
    props: getServiceProps(StatementType.ACCEPTANCE_DEPOSIT),
    meta: {
      layout: Layout.INNER,
      authorized: true,
      breadCrumbs: {
        back: true,
        path: [
          {
            text: RouteText.HOME,
            disabled: false,
            href: RoutePath.HOME,
          },
          {
            text: RouteText.STATEMENTS,
            disabled: false,
            href: RoutePath.STATEMENTS,
          },
          {
            text: mapStatementName[StatementType.ACCEPTANCE_DEPOSIT],
            disabled: true,
          },
        ],
      },
    },
    component: ServiceBody,
  },
  {
    path: 'transfer-el-docs/:statementId?',
    name: RouteName[StatementType.TRANSFER_EL_DOCS],
    props: getServiceProps(StatementType.TRANSFER_EL_DOCS),
    meta: {
      layout: Layout.INNER,
      authorized: true,
      breadCrumbs: {
        back: true,
        path: [
          {
            text: RouteText.HOME,
            disabled: false,
            href: RoutePath.HOME,
          },
          {
            text: RouteText.STATEMENTS,
            disabled: false,
            href: RoutePath.STATEMENTS,
          },
          {
            text: mapStatementName[StatementType.TRANSFER_EL_DOCS],
            disabled: true,
          },
        ],
      },
    },
    component: ServiceBody,
  },
  {
    path: 'correct-translation/:statementId?',
    name: RouteName[StatementType.CORRECT_TRANSLATION],
    props: getServiceProps(StatementType.CORRECT_TRANSLATION),
    meta: {
      layout: Layout.INNER,
      authorized: true,
      breadCrumbs: {
        back: true,
        path: [
          {
            text: RouteText.HOME,
            disabled: false,
            href: RoutePath.HOME,
          },
          {
            text: RouteText.STATEMENTS,
            disabled: false,
            href: RoutePath.STATEMENTS,
          },
          {
            text: mapStatementName[StatementType.CORRECT_TRANSLATION],
            disabled: true,
          },
        ],
      },
    },
    component: ServiceBody,
  },
  {
    path: 'acceptance-escrow/:statementId?',
    name: RouteName[StatementType.ACCEPTANCE_ESCROW],
    props: getServiceProps(StatementType.ACCEPTANCE_ESCROW),
    meta: {
      layout: Layout.INNER,
      authorized: true,
      breadCrumbs: {
        back: true,
        path: [
          {
            text: RouteText.HOME,
            disabled: false,
            href: RoutePath.HOME,
          },
          {
            text: RouteText.STATEMENTS,
            disabled: false,
            href: RoutePath.STATEMENTS,
          },
          {
            text: mapStatementName[StatementType.ACCEPTANCE_ESCROW],
            disabled: true,
          },
        ],
      },
    },
    component: ServiceBody,
  },
  {
    path: 'storage-accept/:statementId?',
    name: RouteName[StatementType.STORAGE_ACCEPT],
    props: getServiceProps(StatementType.STORAGE_ACCEPT),
    meta: {
      layout: Layout.INNER,
      authorized: true,
      breadCrumbs: {
        back: true,
        path: [
          {
            text: RouteText.HOME,
            disabled: false,
            href: RoutePath.HOME,
          },
          {
            text: RouteText.STATEMENTS,
            disabled: false,
            href: RoutePath.STATEMENTS,
          },
          {
            text: mapStatementName[StatementType.STORAGE_ACCEPT],
            disabled: true,
          },
        ],
      },
    },
    component: ServiceBody,
  },
  {
    path: 'storage-issue/:statementId?',
    name: RouteName[StatementType.STORAGE_ISSUE],
    props: getServiceProps(StatementType.STORAGE_ISSUE),
    meta: {
      layout: Layout.INNER,
      authorized: true,
      breadCrumbs: {
        back: true,
        path: [
          {
            text: RouteText.HOME,
            disabled: false,
            href: RoutePath.HOME,
          },
          {
            text: RouteText.STATEMENTS,
            disabled: false,
            href: RoutePath.STATEMENTS,
          },
          {
            text: mapStatementName[StatementType.STORAGE_ISSUE],
            disabled: true,
          },
        ],
      },
    },
    component: ServiceBody,
  },
  {
    path: 'document-conversion/:statementId?',
    name: RouteName[StatementType.DOCUMENT_CONVERSION],
    props: getServiceProps(StatementType.DOCUMENT_CONVERSION),
    meta: {
      layout: Layout.INNER,
      authorized: true,
      breadCrumbs: {
        back: true,
        path: [
          {
            text: RouteText.HOME,
            disabled: false,
            href: RoutePath.HOME,
          },
          {
            text: RouteText.STATEMENTS,
            disabled: false,
            href: RoutePath.STATEMENTS,
          },
          {
            text: mapStatementName[StatementType.DOCUMENT_CONVERSION],
            disabled: true,
          },
        ],
      },
    },
    component: ServiceBody,
  },
  {
    path: 'rod/:statementId?',
    name: RouteName[StatementType.ROD],
    props: getServiceProps(StatementType.ROD),
    meta: {
      layout: Layout.INNER,
      authorized: true,
      breadCrumbs: {
        back: true,
        path: [
          {
            text: RouteText.HOME,
            disabled: false,
            href: RoutePath.HOME,
          },
          {
            text: RouteText.STATEMENTS,
            disabled: false,
            href: RoutePath.STATEMENTS,
          },
          {
            text: mapStatementName[StatementType.ROD],
            disabled: true,
          },
        ],
      },
    },
    component: ServiceBody,
  },
  {
    path: 'extraction-rod/:statementId?',
    name: RouteName[StatementType.EXTRACTION_ROD],
    props: getServiceProps(StatementType.EXTRACTION_ROD),
    meta: {
      layout: Layout.INNER,
      authorized: true,
      breadCrumbs: {
        back: true,
        path: [
          {
            text: RouteText.HOME,
            disabled: false,
            href: RoutePath.HOME,
          },
          {
            text: RouteText.STATEMENTS,
            disabled: false,
            href: RoutePath.STATEMENTS,
          },
          {
            text: mapStatementName[StatementType.EXTRACTION_ROD],
            disabled: true,
          },
        ],
      },
    },
    component: ServiceBody,
  },
  {
    path: 'egrn-confirm/:statementId?',
    name: RouteName[StatementType.EGRN_CONFIRM],
    props: getServiceProps(StatementType.EGRN_CONFIRM),
    meta: {
      layout: Layout.INNER,
      authorized: true,
      breadCrumbs: {
        back: true,
        path: [
          {
            text: RouteText.HOME,
            disabled: false,
            href: RoutePath.HOME,
          },
          {
            text: RouteText.STATEMENTS,
            disabled: false,
            href: RoutePath.STATEMENTS,
          },
          {
            text: mapStatementName[StatementType.EGRN_CONFIRM],
            disabled: true,
          },
        ],
      },
    },
    component: ServiceBody,
  },
];
const alternativeServiceEditChildren = serviceChildren
  .filter((service) => service.name === StatementType.NOTICE_RECOVERY)
  .map((service) => {
    return {
      ...cloneDeep(service),
      path: service.path.replace('statementId', 'originalStatementId'),
      props: getAlternativeServiceEditProps(service.name),
      name: `${RouteName.ALTERNATIVE_SERVICE_EDIT}-${service.name}`,
    };
  });

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      name: RouteName.HOME,
      component: Home,
      meta: { layout: Layout.DEFAULT, authorized: false },
    },
    {
      path: '/service',
      name: RouteName.SERVICE,
      component: Service,
      meta: { layout: Layout.INNER, authorized: true },
      children: serviceChildren,
    },
    {
      path: '/alternative-service',
      component: AltLayout,
      meta: { layout: Layout.INNER, authorized: true },
      children: [
        {
          path: 'refusal/:parentStatementId',
          name: RouteName.ALTERNATIVE_SERVICE_REFUSAL,
          props: true,
          meta: {
            layout: Layout.INNER,
            authorized: true,
            breadCrumbs: {
              back: true,
              path: [
                {
                  text: RouteText.HOME,
                  disabled: false,
                  href: RoutePath.HOME,
                },
                {
                  text: RouteText.STATEMENTS,
                  disabled: false,
                  href: RoutePath.STATEMENTS,
                },
                {
                  // В AltRefusal переустанавливается
                  text: RouteText.STATEMENT_DETAIL,
                  disabled: true,
                },
              ],
            },
          },
          component: AltRefusal,
        },
        {
          path: 'district/:parentStatementId',
          name: RouteName.ALTERNATIVE_SERVICE_DISTRICT,
          props: true,
          meta: {
            layout: Layout.INNER,
            authorized: true,
            breadCrumbs: {
              back: true,
              path: [
                {
                  text: RouteText.HOME,
                  disabled: false,
                  href: RoutePath.HOME,
                },
                {
                  text: RouteText.STATEMENTS,
                  disabled: false,
                  href: RoutePath.STATEMENTS,
                },
                {
                  // В AltDistrict переустанавливается
                  text: RouteText.STATEMENT_DETAIL,
                  disabled: true,
                },
              ],
            },
          },
          component: AltRegion,
        },
        {
          path: 'dop-documents/:altStatementId/applicant',
          name: RouteName.ADD_DOCUMENTS_APPLICANT,
          props: true,
          meta: {
            layout: Layout.INNER,
            authorized: true,
            breadCrumbs: {
              back: true,
            },
          },
          component: AddDocumentsApplicant,
        },
        {
          path: 'dop-documents/:altStatementId/edit',
          name: RouteName.ADD_DOCUMENTS_EDIT,
          props: true,
          meta: {
            layout: Layout.INNER,
            authorized: true,
            breadCrumbs: {
              back: true,
            },
          },
          component: AddDocumentsEdit,
        },
        {
          path: 'dop-documents/:altStatementId/sign',
          name: RouteName.ADD_DOCUMENTS_SIGN,
          props: true,
          meta: {
            layout: Layout.INNER,
            authorized: true,
            breadCrumbs: {
              back: true,
            },
          },
          component: AddDocumentsSign,
        },
        {
          path: 'additional-signers/:statementId',
          name: RouteName.ADDITIONAL_SIGNERS,
          props: true,
          meta: {
            layout: Layout.INNER,
            authorized: true,
            breadCrumbs: {
              back: true,
              path: [
                {
                  text: RouteText.HOME,
                  disabled: false,
                  href: RoutePath.HOME,
                },
                {
                  text: RouteText.STATEMENTS,
                  disabled: false,
                  href: RoutePath.STATEMENTS,
                },
                {
                  text: RouteText.STATEMENT_DETAIL,
                  disabled: true,
                },
              ],
            },
          },
          component: AdditionalSigners,
        },
        {
          path: 'edit',
          name: 'ALTERNATIVE_SERVICE_EDIT',
          component: Service,
          meta: {
            layout: Layout.INNER,
            authorized: true,
            breadCrumbs: {
              back: true,
            },
          },
          children: alternativeServiceEditChildren,
        },
        {
          path: 'recend/:altStatementId/applicant',
          name: RouteName.RESEND_TO_FSSP_APPLICANT,
          props: true,
          meta: {
            layout: Layout.INNER,
            authorized: true,
            breadCrumbs: {
              back: true,
            },
          },
          component: ResendToFsspApplicant,
        },
        {
          path: 'recend/:altStatementId/sign',
          name: RouteName.RESEND_TO_FSSP_SIGN,
          props: true,
          meta: {
            layout: Layout.INNER,
            authorized: true,
            breadCrumbs: {
              back: true,
            },
          },
          component: ResendToFsspSign,
        },
      ],
    },
    {
      path: '/profile',
      name: RouteName.PROFILE,
      component: Profile,
      meta: {
        layout: Layout.INNER,
        menu: true,
        authorized: true,
        breadCrumbs: {
          back: false,
          path: [
            {
              text: RouteText.HOME,
              disabled: false,
              href: RoutePath.HOME,
            },
            {
              text: RouteText.STATEMENTS,
              disabled: true,
            },
          ],
        },
      },
      children: [
        {
          path: 'statements/',
          name: RouteName.STATEMENTS,
          component: MyStatements,
          meta: {
            layout: Layout.INNER,
            menu: true,
            authorized: true,
            breadCrumbs: {
              back: false,
              path: [
                {
                  text: RouteText.HOME,
                  disabled: false,
                  href: RoutePath.HOME,
                },
                {
                  text: RouteText.STATEMENTS,
                  disabled: true,
                },
              ],
            },
          },
        },
        {
          path: 'statements/:id',
          name: RouteName.STATEMENT_DETAIL,
          component: StatementDetail,
          meta: {
            layout: Layout.INNER,
            authorized: true,
            breadCrumbs: {
              back: true,
              path: [
                {
                  text: RouteText.HOME,
                  disabled: false,
                  href: RoutePath.HOME,
                },
                {
                  text: RouteText.STATEMENTS,
                  disabled: false,
                  href: RoutePath.STATEMENTS,
                },
                {
                  text: RouteText.STATEMENT_DETAIL,
                  disabled: true,
                },
              ],
            },
          },
        },
        {
          path: 'statements/:statementId/detail/',
          name: RouteName.FORM_VIEW,
          component: FormView,
          props: (route: Route) => ({
            statementId: route.params.statementId,
            hasTitle: true,
          }),
          meta: {
            layout: Layout.INNER,
            authorized: true,
            breadCrumbs: {
              back: true,
              path: [
                {
                  text: RouteText.HOME,
                  disabled: false,
                  href: RoutePath.HOME,
                },
                {
                  text: RouteText.STATEMENTS,
                  disabled: false,
                  href: RoutePath.STATEMENTS,
                },
                {
                  text: 'Детали заявления',
                  disabled: true,
                },
              ],
            },
          },
        },
        {
          path: 'personal-details/',
          name: RouteName.PERSONAL_DETAILS,
          component: PersonalDetails,
          meta: {
            layout: Layout.INNER,
            menu: true,
            authorized: true,
            breadCrumbs: {
              back: false,
              path: [
                {
                  text: RouteText.HOME,
                  disabled: false,
                  href: RoutePath.HOME,
                },
                {
                  text: RouteText.PERSONAL_DETAILS,
                  disabled: true,
                },
              ],
            },
          },
        },
        {
          path: 'statements-joint/',
          name: RouteName.STATEMENTS_JOINT,
          component: StatementsJoint,
          meta: {
            layout: Layout.INNER,
            menu: true,
            authorized: true,
            breadCrumbs: {
              back: false,
              path: [
                {
                  text: RouteText.HOME,
                  disabled: false,
                  href: RoutePath.HOME,
                },
                {
                  text: RouteText.STATEMENTS_JOINT,
                  disabled: true,
                },
              ],
            },
          },
        },
        {
          path: 'organization-statements/',
          name: RouteName.ORGANIZATION_STATEMENTS,
          component: OrganizationStatements,
          meta: {
            layout: Layout.INNER,
            menu: true,
            authorized: true,
            breadCrumbs: {
              back: false,
              path: [
                {
                  text: RouteText.HOME,
                  disabled: false,
                  href: RoutePath.HOME,
                },
                {
                  text: RouteText.ORGANIZATION_STATEMENTS,
                  disabled: true,
                },
              ],
            },
          },
        },
        {
          path: 'notifications/',
          name: RouteName.NOTIFICATIONS,
          component: Notifications,
          meta: {
            layout: Layout.INNER,
            menu: true,
            authorized: true,
            breadCrumbs: {
              back: false,
              path: [
                {
                  text: RouteText.HOME,
                  disabled: false,
                  href: RoutePath.HOME,
                },
                {
                  text: RouteText.NOTIFICATIONS,
                  disabled: true,
                },
              ],
            },
          },
        },
        {
          path: 'notifications/:id/',
          name: RouteName.NOTIFICATION_DETAIL,
          component: NotificationDetail,
          meta: {
            layout: Layout.INNER,
            menu: false,
            authorized: true,
            breadCrumbs: {
              back: true,
              path: [
                {
                  text: RouteText.HOME,
                  disabled: false,
                  href: RoutePath.HOME,
                },
                {
                  text: RouteText.NOTIFICATIONS,
                  disabled: false,
                  href: '/profile/notifications',
                },
                {
                  text: 'Детали',
                  disabled: true,
                },
              ],
            },
          },
        },
        {
          path: 'documents-and-data/',
          name: RouteName.DOCUMENTS_AND_DATA,
          component: DocumentsAndData,
          meta: {
            layout: Layout.INNER,
            menu: true,
            authorized: true,
            breadCrumbs: {
              back: false,
              path: [
                {
                  text: RouteText.HOME,
                  disabled: false,
                  href: RoutePath.HOME,
                },
                {
                  text: RouteText.DOCUMENTS_AND_DATA,
                  disabled: true,
                },
              ],
            },
          },
        },
        {
          path: 'notifications-documents',
          name: RouteName.NOTIFICATIONS_DOCUMENTS,
          component: NotificationsDocuments,
          meta: {
            layout: Layout.INNER,
            menu: true,
            authorized: true,
            breadCrumbs: {
              back: false,
              path: [
                {
                  text: RouteText.HOME,
                  disabled: false,
                  href: RoutePath.HOME,
                },
                {
                  text: RouteText.NOTIFICATIONS_DOCUMENTS,
                  disabled: true,
                },
              ],
            },
          },
        },
        {
          path: 'notifications-documents/:id',
          name: RouteName.NOTIFICATIONS_DOCUMENTS_DETAIL,
          component: NotificationsDocumentsDetail,
          props: true,
          meta: {
            layout: Layout.INNER,
            authorized: true,
            breadCrumbs: {
              back: true,
              path: [
                {
                  text: RouteText.HOME,
                  disabled: false,
                  href: RoutePath.HOME,
                },
                {
                  text: RouteText.NOTIFICATIONS_DOCUMENTS,
                  disabled: false,
                  href: '/profile/notifications-documents',
                },
                {
                  text: 'Просмотр документов',
                  disabled: true,
                },
              ],
            },
          },
        },
      ],
    },
    {
      path: '/faq',
      name: RouteName.FAQ,
      component: FAQ,
      meta: {
        layout: Layout.INNER,
        authorized: false,
        breadCrumbs: {
          back: true,
          path: [
            {
              text: RouteText.HOME,
              disabled: false,
              href: RoutePath.HOME,
            },
            {
              text: 'Частые вопросы',
              disabled: true,
            },
          ],
        },
      },
    },
    {
      path: '*',
      component: Home,
      meta: { layout: Layout.DEFAULT },
    },
  ],
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

async function onShowModalWindowGlobal(pathName: any) {
  let VALID_NAME_URL = [
    'home',
    'profile',
    'Statements',
    'faq',
    'DocumentsAndData',
    'Notifications',
  ];
  VALID_NAME_URL = VALID_NAME_URL.map((item: any) => item.toUpperCase());
  if (window.screen.width <= 960) {
    return !(pathName && VALID_NAME_URL.includes(pathName.toUpperCase()));
  }
  return false;
}

router.beforeEach(async (to, from, next) => {
  const metaAuthorized = to.matched.some((record) => record.meta.authorized);
  ErrorAlertModule.setErrorAlert('');
  ErrorAlertModule.setErrorAlertXml('');

  if (!AuthModule.isAuthenticated) {
    await AuthModule.getResource();
  }

  if (metaAuthorized) {
    if (AuthModule.isAuthenticated) {
      await NotificationModule.getUserTopNotifications();
      await NotificationDocModule.getNotificationsDocNew();
      next();
    } else {
      next({ name: RouteName.HOME });
    }
  } else {
    next();
  }

  const showModal = await onShowModalWindowGlobal(to.name);
  if (showModal) {
    await router.replace('/');
    if (to.meta.breadCrumbs.path) {
      const pageName =
        to.meta.breadCrumbs.path[to.meta.breadCrumbs.path.length - 1].text;
      await OtherModule.onChangePageNameInModalWindowFull(pageName);
    }
    await OtherModule.onShowModalWindowFull(showModal);
  }
});

router.afterEach(() => {
  BreadCrumbsModule.SET(null);
});

export default router;
